/**
 * Turns a finished run into a CSV, so the outcome of every row survives leaving the page.
 */

import type { RowResult } from "./runUpload.js";

const HEADERS = [
  "line",
  "company_uuid",
  "reference_value",
  "label",
  "outcome",
  "http_status",
  "detail",
] as const;

/**
 * Leading characters that make Excel and Sheets treat a cell as a formula. The label
 * column comes straight from an uploaded file and the detail column carries API
 * messages, so both are untrusted as far as this export is concerned.
 */
const FORMULA_TRIGGERS = ["=", "+", "-", "@", "\t", "\r"];

/**
 * Quotes a field only when it needs it, and doubles any embedded quote — firm names
 * carry commas and ampersands, and the detail column carries API messages.
 *
 * A field starting with a formula trigger is prefixed with a single quote so the
 * spreadsheet reads it as text. Quoting alone does not prevent this: Excel strips the
 * quotes and evaluates what is inside.
 */
function csvField(value: string | number | null): string {
  let text = value === null ? "" : String(value);
  if (FORMULA_TRIGGERS.some(trigger => text.startsWith(trigger))) {
    text = `'${text}`;
  }
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function buildResultsCsv(results: RowResult[]): string {
  const lines = [HEADERS.join(",")];
  for (const result of results) {
    lines.push(
      [
        result.row.lineNumber,
        result.row.companyUuid,
        result.row.referenceValue,
        result.row.label,
        result.outcome,
        result.status,
        result.detail,
      ]
        .map(csvField)
        .join(","),
    );
  }
  return `${lines.join("\n")}\n`;
}

export function resultsFilename(referenceType: string, now: Date): string {
  const stamp = now.toISOString().slice(0, 19).replace(/[:T]/g, "-");
  return `${referenceType}-upload-${stamp}.csv`;
}

/**
 * Builds the CSV and hands it to the browser as a download.
 *
 * Shared so a saved run exports exactly what a live one does — the alternative was a
 * second copy of the blob/anchor dance, and the two would drift.
 *
 * @param at Stamps the filename. Pass a run's start time so an exported historical run
 *   is named after the run rather than after the moment it was downloaded.
 */
export function downloadResultsCsv(results: RowResult[], referenceType: string, at: Date): void {
  const blob = new Blob([buildResultsCsv(results)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = resultsFilename(referenceType, at);
  anchor.click();
  // Deferred: revoking in the same tick can cancel the download before it starts.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
